/*
  SolveX welcome offer popup.

  Owns only presentation and suppression. The email itself is submitted by
  Shopify's native customer form, which reloads the page; a marker element
  rendered inside the {% form %} block on success is the signal to open in the
  reward step and record the claim.
*/

(function () {
  'use strict';

  var STORAGE_KEY = 'solvex:welcome-offer:v1';
  var SESSION_KEY = 'solvex:welcome-offer:session';
  var SUBMITTED_KEY = 'solvex:welcome-offer:submitted';
  var COOKIE_NAME = 'solvex_offer_dismissed';
  var DAY_MS = 24 * 60 * 60 * 1000;

  /*
    Two stores, because a claim and a dismissal are not the same promise.

    A claim is permanent and belongs in localStorage: that visitor has the code
    and must never be asked again. Being shown the offer is only "not again this
    visit", and lives in a session cookie.

    Both are written the moment the offer is SHOWN, never when it is closed —
    see markSeen(). Gating on an explicit dismissal meant a visitor who just
    kept browsing was shown it on every page.
  */

  function readState() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      // Private mode, blocked storage, or corrupt JSON. Treat as first visit.
      return null;
    }
  }

  function writeState(status) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ status: status, at: Date.now() }));
    } catch (error) {
      // Storage unavailable. The popup still works for this page view.
    }
  }

  /*
    A dismissal is remembered for the rest of the visit, in a session cookie
    first and sessionStorage second.

    The cookie is what makes this behave the way the offer is meant to:
    sessionStorage is scoped to a single TAB, so a visitor who opens the shop in
    a second tab gets a clean slate and is shown the offer again — which reads
    as "it comes back on every page". A session cookie is shared across tabs and
    is dropped when the browser closes, which is exactly "not again until they
    come back to the site". (Since 2026-09-28 a new entry also clears it; see
    startOrContinueVisit below.)

    sessionStorage is kept as a fallback for contexts where cookies are refused.
  */

  function hasDismissalCookie() {
    try {
      return document.cookie.split(';').some(function (part) {
        return part.trim().indexOf(COOKIE_NAME + '=1') === 0;
      });
    } catch (error) {
      return false;
    }
  }

  function readSessionDismissal() {
    if (hasDismissalCookie()) return true;

    try {
      return window.sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function writeSessionDismissal() {
    try {
      // No Expires/Max-Age, so the browser drops it at the end of the session.
      document.cookie = COOKIE_NAME + '=1; path=/; SameSite=Lax';
    } catch (error) {
      // Cookies refused; sessionStorage below still covers this tab.
    }

    try {
      window.sessionStorage.setItem(SESSION_KEY, '1');
    } catch (error) {
      // Storage unavailable.
    }
  }

  /*
    What counts as a new entry to the store (merchant, 2026-09-28: the offer is
    to appear on the homepage again on every entry to the site). A session
    cookie alone did not do it: phone browsers keep a "session" alive for days
    in the background, so a visitor coming back the next day was never
    offered it again. The dismissal above is therefore cleared when either:
    - the page was reached from outside the store (a link, a search result, an
      ad, a typed or bookmarked address) by a normal navigation, not a reload
      or back/forward; or
    - there has been no page view in the store for 30 minutes.
    Moving between pages inside the store, a reload and the return from
    Shopify's captcha challenge (same host) all stay in the same visit.
  */
  var ACTIVITY_COOKIE = 'solvex_offer_activity';
  var VISIT_GAP_MS = 30 * 60 * 1000;

  function arrivedFromOutside() {
    var type = 'navigate';
    try {
      var entry = performance.getEntriesByType('navigation')[0];
      if (entry && entry.type) type = entry.type;
    } catch (error) {
      // Older browser: treat as a normal navigation.
    }
    if (type !== 'navigate') return false;
    try {
      return !document.referrer || new URL(document.referrer).host !== window.location.host;
    } catch (error) {
      return true;
    }
  }

  function readActivity() {
    try {
      var part = document.cookie.split(';').map(function (item) { return item.trim(); })
        .filter(function (item) { return item.indexOf(ACTIVITY_COOKIE + '=') === 0; })[0];
      return part ? Number(part.split('=')[1]) || 0 : 0;
    } catch (error) {
      return 0;
    }
  }

  function writeActivity() {
    try {
      document.cookie = ACTIVITY_COOKIE + '=' + Date.now() + '; path=/; max-age=86400; SameSite=Lax';
    } catch (error) {
      // Cookies refused: every entry then looks like a continuing visit only
      // within this tab, through sessionStorage.
    }
  }

  function clearSessionDismissal() {
    try {
      document.cookie = COOKIE_NAME + '=; path=/; max-age=0; SameSite=Lax';
    } catch (error) {
      // Cookies refused.
    }
    try {
      window.sessionStorage.removeItem(SESSION_KEY);
    } catch (error) {
      // Storage unavailable.
    }
  }

  // Runs on every page view, before anything reads the dismissal.
  function startOrContinueVisit() {
    var last = readActivity();
    if (arrivedFromOutside() || (last && Date.now() - last > VISIT_GAP_MS)) clearSessionDismissal();
    writeActivity();
  }

  /*
    Shopify returns a customer-form submission, and the return from its
    captcha challenge, to the page with "#contact_form" appended. That is the
    default id of the footer newsletter form, so the browser jumped down to the
    footer, next to the "Stay in the glow" band, while the offer's own result
    shows at the top (merchant report, 2026-09-28). When this dialog handles
    the return, the hash is dropped and the page stays at the top. It runs now
    and again at load, because the browser may apply the fragment late.
  */
  function stayAtTop() {
    var reset = function () {
      if (window.location.hash !== '#contact_form') return;
      try {
        window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
      } catch (error) {
        // Old browser: the jump below still applies.
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    };
    reset();
    if (document.readyState !== 'complete') window.addEventListener('load', reset, { once: true });
  }

  function readSubmitted() {
    try {
      return window.sessionStorage.getItem(SUBMITTED_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function clearSubmitted() {
    try {
      window.sessionStorage.removeItem(SUBMITTED_KEY);
    } catch (error) {
      // Storage unavailable.
    }
  }

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  /*
    The SOLVEX loading screen covers the page: `html.site-loading` hides every
    body child except the loader itself. Opening behind it would show the
    visitor nothing while still burning the once-per-visit marker, so the offer
    would be silently spent on a page they never saw it on.

    It clears on `window.load` — not DOMContentLoaded — or a 5s fallback in
    site-preferences.js, which is routinely longer than the popup's delay.
  */
  function isSiteLoading() {
    return document.documentElement.classList.contains('site-loading');
  }

  /*
    Shopify's bot-protection challenge is rendered inside the theme layout, so
    the popup can land on top of the "I am human" control and block it. The
    Liquid guard on request.page_type is the primary defence; this is a second
    one that does not depend on that string staying correct, because the cost
    of being wrong is a visitor who cannot complete the form at all.

    Detection must key on the challenge page itself, never on the presence of a
    captcha widget. hCaptcha can be present on any page (it was loaded on
    every page while the form opted into `data-shopify-captcha`), and an
    earlier version of this looked for an hCaptcha iframe and consequently
    suppressed the popup site-wide.

    `#captcha_form` is Shopify's challenge form and carries `data-nocaptcha`, so
    it exists only on that page.
  */
  function isChallengePage() {
    if (document.getElementById('captcha_form')) return true;

    try {
      return window.location.pathname.indexOf('/challenge') === 0;
    } catch (error) {
      return false;
    }
  }

  class SolvexWelcomeOffer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-solvex-welcome-offer-dialog]');
      this.overlay = this.querySelector('[data-solvex-welcome-offer-overlay]');
      this.closeButton = this.querySelector('[data-solvex-welcome-offer-close]');
      this.copyButton = this.querySelector('[data-solvex-welcome-offer-copy]');
      this.copyLabel = this.querySelector('[data-solvex-welcome-offer-copy-label]');
      this.status = this.querySelector('[data-solvex-welcome-offer-status]');
      this.signupStep = this.querySelector('[data-solvex-welcome-offer-step="signup"]');
      this.rewardStep = this.querySelector('[data-solvex-welcome-offer-step="reward"]');

      this.code = this.dataset.discountCode || '';
      this.openerElement = null;
      this.timer = null;

      this.form = this.querySelector('form');

      this.onKeydown = this.onKeydown.bind(this);

      if (this.closeButton) this.closeButton.addEventListener('click', this.close.bind(this));
      if (this.overlay) this.overlay.addEventListener('click', this.close.bind(this));
      if (this.copyButton) this.copyButton.addEventListener('click', this.copyCode.bind(this));
      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));

      /*
        Any link to #welcome-offer opens the offer on demand; the announcement
        bar's "$10 off" item uses it (merchant request, 2026-09-24). Bound on
        the document so the marquee's cloned copies of the link work too.
      */
      this.onOfferLinkClick = this.onOfferLinkClick.bind(this);
      document.addEventListener('click', this.onOfferLinkClick);

      if (!isDesignMode()) startOrContinueVisit();
      this.schedule();
      // The photograph is only fetched ahead of time where the offer opens by
      // itself. Elsewhere it is placed when the dialog is opened on request.
      if (this.dataset.autoOpen === 'true') this.placeImageWhenIdle();
    }

    disconnectedCallback() {
      this.stopWaiting();
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onOfferLinkClick);
    }

    /*
      An explicit request always opens, whatever this visit's suppression
      says. A visitor who already claimed the code sees the code again rather
      than the signup form.
    */
    openOnRequest() {
      this.stopWaiting();

      var state = readState();
      if (state && state.status === 'claimed') this.showReward();
      this.openWhenReady();
    }

    onOfferLinkClick(event) {
      var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
      if (!link || link.hash !== '#welcome-offer') return;

      event.preventDefault();
      this.openOnRequest();
    }

    /*
      The visitor pressed the button. Take the offer off the screen straight
      away rather than leaving the form sitting there through the navigation -
      and, more importantly, do not let it reappear over Shopify's bot
      challenge, which is where that submit may well land.
    */
    onSubmit() {
      try {
        window.sessionStorage.setItem(SUBMITTED_KEY, '1');
      } catch (error) {
        // Storage unavailable; the immediate hide below still happens.
      }

      if (this.timer) clearTimeout(this.timer);
      this.classList.add('solvex-welcome-offer--submitting');
      this.close();
    }

    /* Decide whether and when this visitor should see the popup. */
    schedule() {
      /*
        In the Theme Editor the offer opens only while its section is selected.

        It used to open on load in design mode so the merchant could style it —
        but the editor reloads the preview on every page change and on many
        interactions, so it reopened constantly, which is indistinguishable from
        the popup being broken. `shopify:section:select` is Shopify's mechanism
        for exactly this and keeps it fully stylable.
      */
      if (isDesignMode()) {
        this.bindDesignMode();
        return;
      }

      // Returning from a successful signup. Show the code and never ask again.
      // The marker is rendered inside the {% form %} block, which is the only
      // place Liquid exposes `form.posted_successfully?`.
      if (this.querySelector('[data-solvex-welcome-offer-posted]')) {
        stayAtTop();
        writeState('claimed');
        clearSubmitted();
        this.showReward();
        this.openWhenReady();
        return;
      }

      // A rejected signup must be shown, or the visitor never finds out why it
      // failed - the error only exists inside this dialog.
      if (this.querySelector('.solvex-welcome-offer__error')) {
        stayAtTop();
        clearSubmitted();
        this.openWhenReady();
        return;
      }

      if (this.handleCaptchaReturn()) return;

      if (isChallengePage()) return;

      // Arriving on a link to /#welcome-offer, for example from an email.
      if (window.location.hash === '#welcome-offer') {
        this.openOnRequest();
        return;
      }

      /*
        The offer opens by itself on the homepage only (merchant decision,
        2026-09-28). Every other page still renders the dialog, so the paths
        above (a signup result, a captcha return, a #welcome-offer link) keep
        working wherever they land.
      */
      if (this.dataset.autoOpen !== 'true') return;

      // Mid-signup: the visitor is somewhere between pressing the button and
      // the confirmation. Do not interrupt them with the offer again.
      if (readSubmitted()) return;

      var state = readState();

      // The only permanent suppression. Everything else is a pause.
      if (state && state.status === 'claimed') return;

      if (this.dataset.dismissBehaviour === 'days') {
        if (state && state.status === 'dismissed') {
          var days = parseInt(this.dataset.reappearDays, 10);
          if (!isFinite(days) || days < 1) days = 14;
          if (Date.now() - Number(state.at || 0) < days * DAY_MS) return;
        }
      } else if (readSessionDismissal()) {
        return;
      }

      /*
        The delay is configured in milliseconds, not seconds, because Shopify
        rounds a decimal `number` setting to one decimal place: 0.67 came back
        from the API stored as 0.7. An integer count of milliseconds is kept
        exactly, so sub-second delays are actually honoured.
      */
      var delay = parseInt(this.dataset.delayMs, 10);
      if (!isFinite(delay) || delay < 0) delay = 3000;

      /*
        A plain timer from arrival on the homepage: delay_ms, set to 9000 (nine
        seconds) by the merchant on 2026-09-28. Scrolling or touching the page
        does not bring it forward, at the merchant's request. Waiting this long
        also keeps its photograph and hCaptcha out of the page's own loading
        (audit PERF-01).
      */
      // Counted from arrival (the start of the page load), not from when this
      // element was parsed, which on a slow phone is seconds later.
      var sinceArrival = window.performance && performance.now ? performance.now() : 0;
      this.timer = setTimeout(this.openWhenReady.bind(this), Math.max(0, delay - sinceArrival));
    }

    stopWaiting() {
      clearTimeout(this.timer);
      this.timer = null;
    }

    /*
      The photograph sits in a <template>, so the browser does not fetch it
      with the page. It is put in place after the page has loaded and the
      browser is idle, long before the nine-second timer opens the dialog, or
      at once when the dialog opens earlier on request.
    */
    placeImage() {
      var template = this.querySelector('template[data-solvex-welcome-offer-image]');
      if (!template) return;
      template.replaceWith(template.content.cloneNode(true));
    }

    placeImageWhenIdle() {
      var self = this;
      var place = function () {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(function () { self.placeImage(); }, { timeout: 3000 });
        } else {
          window.setTimeout(function () { self.placeImage(); }, 1000);
        }
      };
      if (document.readyState === 'complete') place();
      else window.addEventListener('load', place, { once: true });
    }

    /*
      Coming back from Shopify's captcha challenge.

      Shopify's own form script cancels the original submit, stashes the field
      values in sessionStorage, and sends the visitor to the challenge. On the
      way back it returns them here with `form_key` and `form_type` in the query
      string and refills the form on DOMContentLoaded — but it does NOT resubmit.
      The visitor has to press the button again.

      That only works if the dialog is on screen. Without this the popup stayed
      hidden behind the `submitted` flag, the refilled form was invisible, and
      the signup looked like it silently did nothing — which is exactly the loop
      the merchant hit.
    */
    handleCaptchaReturn() {
      var params;

      try {
        params = new URLSearchParams(window.location.search);
      } catch (error) {
        return false;
      }

      if (!params.get('form_key') || params.get('form_type') !== 'customer') return false;

      clearSubmitted();

      stayAtTop();
      var reveal = this.openWhenReady.bind(this);

      // Open after Shopify has refilled the fields, so the visitor sees their
      // address already in place rather than an empty form.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', reveal, { once: true });
      } else {
        setTimeout(reveal, 0);
      }

      return true;
    }

    showReward() {
      if (this.signupStep) this.signupStep.hidden = true;
      if (this.rewardStep) this.rewardStep.hidden = false;
    }

    isOpen() {
      return this.classList.contains('solvex-welcome-offer--open');
    }

    /* Theme Editor only: open while this section is selected, close when it is not. */
    bindDesignMode() {
      var self = this;

      document.addEventListener('shopify:section:select', function (event) {
        if (event.target && event.target.contains(self)) self.open();
      });

      document.addEventListener('shopify:section:deselect', function (event) {
        if (event.target && event.target.contains(self)) self.close();
      });
    }

    /*
      Mark the offer as already shown for this visitor.

      Never in the Theme Editor: the merchant needs it to open on every page
      while they are styling it, and design mode must not write suppression
      into their own browser.

      A claim outranks this and is never downgraded.
    */
    markSeen() {
      if (isDesignMode()) return;

      var state = readState();
      if (state && state.status === 'claimed') return;

      if (this.dataset.dismissBehaviour === 'days') {
        writeState('dismissed');
      } else {
        writeSessionDismissal();
      }
    }

    /*
      Open, but never while the loading screen is up — wait for it to clear and
      open then. Every path that opens the popup goes through here, including
      the ones that fire on page load: a successful signup, a validation error,
      and the return from a captcha challenge.
    */
    openWhenReady() {
      if (isDesignMode() || !isSiteLoading()) {
        this.open();
        return;
      }

      if (this.waitingForLoader) return;
      this.waitingForLoader = true;

      var self = this;

      var proceed = function () {
        if (!self.waitingForLoader || isSiteLoading()) return;

        self.waitingForLoader = false;
        self.loaderObserver.disconnect();
        clearTimeout(self.loaderFallback);
        self.open();
      };

      this.loaderObserver = new MutationObserver(proceed);
      this.loaderObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });

      // If the loader never clears, the offer is simply not shown. That is the
      // point: it must not appear over the loading screen.
      this.loaderFallback = setTimeout(function () {
        self.waitingForLoader = false;
        self.loaderObserver.disconnect();
      }, 15000);
    }

    /*
      Start Shopify's invisible hCaptcha for this form now that it is on
      screen, instead of on page load (see the note on the form in the
      section file). Shopify's storefront form script binds a customer form on
      a `change` or `focusin` event from inside it, through a document-level
      listener, and this uses that same path so the widget is bound exactly as
      Shopify binds it on first focus — invisible mode included. The script
      skips forms it has already bound, so a second call does nothing.

      It waits for the page's load event and then for an idle moment: on a
      first visit the popup can open while the page is still being drawn, and
      hCaptcha's script is heavy enough to hold up that first paint on a slow
      phone. The visitor is reading the offer meanwhile, so nothing is lost.
    */
    bindCaptcha() {
      var form = this.form;
      if (!form || form.dataset.cptcha) return;
      var field = form.querySelector('input[type="email"]');
      if (!field) return;

      function bind() {
        if (form.dataset.cptcha) return;
        try {
          field.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (error) {
          // Old browser without the Event constructor: Shopify still binds on
          // the visitor's first focus, as it does for every other form.
        }
      }

      function whenIdle() {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(bind, { timeout: 2000 });
        } else {
          window.setTimeout(bind, 300);
        }
      }

      if (document.readyState === 'complete') {
        whenIdle();
      } else {
        window.addEventListener('load', whenIdle, { once: true });
      }
    }

    open() {
      if (this.isOpen()) return;

      // Defensive: nothing should reach open() while the loader is up, but if
      // it does, do not paint an invisible dialog and mark the offer as seen.
      if (!isDesignMode() && isSiteLoading()) return;

      // Re-checked here, not only in schedule(). The captcha widget may not
      // exist yet when the element connects, and this fires seconds later — by
      // which time the page may have become a challenge the popup must not
      // cover.
      if (!isDesignMode() && isChallengePage()) return;

      /*
        Record the offer as seen the moment it appears, NOT when it is closed.

        Suppression used to hang off close(), which only runs if the visitor
        actually presses X, the overlay or Escape. Someone who simply reads the
        offer and carries on browsing never triggered it, so the popup opened
        again on every single page — the behaviour the merchant reported, and
        reproduced here: two page views, offer shown twice, nothing recorded.

        Shown-once-per-visit is the promise, and it must not depend on the
        visitor dismissing it politely.
      */
      this.markSeen();
      this.stopWaiting();
      this.placeImage();

      this.openerElement = document.activeElement;

      // Force a synchronous reflow so the transition has a start value, then
      // set the class immediately. requestAnimationFrame was used here first,
      // but it is throttled in background tabs and does not fire at all in some
      // headless contexts — which leaves the dialog open at opacity 0, invisible
      // but still blocking the page. A forced reflow has neither problem.
      void this.offsetHeight;
      this.classList.add('solvex-welcome-offer--open');

      this.bindCaptcha();

      this.lockScroll();
      document.addEventListener('keydown', this.onKeydown);

      this.makePageInert();

      var focusTarget = this.rewardStep && !this.rewardStep.hidden ? this.copyButton : this.querySelector('input[type="email"]');

      /*
        Focus is kept inside the dialog by onKeydown and by making the rest of
        the page inert, not by Dawn's trapFocus. trapFocus lists the focusable
        elements once, at open, including those in the hidden reward step, so
        its "last" element was a hidden button that Tab never reached, and Tab
        walked straight out to the hCaptcha links and the skip link (audit
        A11Y-01). It also called setSelectionRange on a refilled email field,
        which Chrome refuses, on the return from the captcha challenge.
      */
      try {
        (focusTarget || this.dialog).focus();
      } catch (error) {
        // Focus is a convenience; the dialog is open and usable regardless.
      }
    }

    /*
      While the dialog is open, everything else on the page is inert, so
      neither Tab nor a screen reader's virtual cursor can reach it. The
      section wrapper that holds the dialog is spared. hCaptcha's own elements
      are too: its challenge window must stay usable, and it is appended to
      <body> outside this section. Only elements this method made inert are
      released on close, so another component's inert state is left alone.
    */
    makePageInert() {
      var self = this;
      this.inertElements = [];
      Array.prototype.forEach.call(document.body.children, function (element) {
        if (element.contains(self) || element.inert) return;
        if (/^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/.test(element.tagName)) return;
        if (/hcaptcha/i.test(element.id || '') || element.querySelector('iframe[src*="hcaptcha"]')) return;
        element.inert = true;
        self.inertElements.push(element);
      });
    }

    releasePage() {
      (this.inertElements || []).forEach(function (element) {
        element.inert = false;
      });
      this.inertElements = [];
    }

    focusableInDialog() {
      var selector = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return Array.prototype.filter.call(this.dialog.querySelectorAll(selector), function (element) {
        return !element.closest('[hidden]') && element.getClientRects().length > 0;
      });
    }

    close() {
      if (!this.isOpen()) return;

      this.classList.remove('solvex-welcome-offer--open');
      document.removeEventListener('keydown', this.onKeydown);
      this.releasePage();

      this.unlockScroll();

      /*
        Focus goes back to whatever had it before the dialog opened. The offer
        usually opens on a timer, when that is just <body>; then focus moves to
        the main content, so the next Tab starts from the page, not from the
        top of the document.
      */
      var opener = this.openerElement;
      var target = opener && opener !== document.body && document.contains(opener) && !opener.closest('[inert]') ? opener : document.getElementById('MainContent');
      if (target && typeof target.focus === 'function') {
        try {
          target.focus({ preventScroll: true });
        } catch (error) {
          // Nothing to restore focus to; the page is usable regardless.
        }
      }

      // Nothing is recorded here. open() marks the offer as seen, so closing —
      // however it happens, and including a submit — needs no bookkeeping. The
      // paths that must still be able to reopen (a successful signup, a
      // validation error, the return from a captcha challenge) call open()
      // directly and bypass the suppression checks in schedule().

      // Removing the open class is the whole hide: the closed state is
      // `visibility: hidden; pointer-events: none`, so the form stays laid out
      // and bindable for Shopify's captcha. Nothing further to do.
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      // Worked out on every key press, because the step shown (signup or
      // reward) and the captcha binding can change what is focusable.
      var focusable = this.focusableInDialog();
      if (!focusable.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = document.activeElement;
      var inside = this.dialog.contains(active);

      if (event.shiftKey && (active === first || !inside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !inside)) {
        event.preventDefault();
        first.focus();
      }
    }

    lockScroll() {
      this.scrollPosition = window.pageYOffset;
      document.body.style.top = '-' + this.scrollPosition + 'px';
      document.body.classList.add('solvex-welcome-offer-open');
    }

    unlockScroll() {
      document.body.classList.remove('solvex-welcome-offer-open');
      document.body.style.top = '';
      window.scrollTo(0, this.scrollPosition || 0);
    }

    copyCode() {
      var self = this;

      var succeed = function () {
        if (self.copyLabel) self.copyLabel.textContent = 'Copied';
        if (self.status) self.status.textContent = 'Code copied to your clipboard.';

        setTimeout(function () {
          if (self.copyLabel) self.copyLabel.textContent = 'Copy code';
        }, 2400);
      };

      var fail = function () {
        if (self.status) self.status.textContent = 'Copy did not work. The code is selected — press Ctrl+C or Cmd+C.';
        self.selectCode();
      };

      // Legacy path. Still needed as a fallback, not only as a polyfill: the
      // async clipboard API rejects when the document lacks focus or the
      // permission is denied, and execCommand often succeeds in those cases.
      var copyWithExecCommand = function () {
        try {
          var field = document.createElement('textarea');
          field.value = self.code;
          field.setAttribute('readonly', '');
          field.style.position = 'fixed';
          field.style.top = '0';
          field.style.opacity = '0';
          document.body.appendChild(field);
          field.select();
          var copied = document.execCommand('copy');
          document.body.removeChild(field);
          return copied;
        } catch (error) {
          return false;
        }
      };

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(this.code).then(succeed, function () {
          copyWithExecCommand() ? succeed() : fail();
        });
        return;
      }

      copyWithExecCommand() ? succeed() : fail();
    }

    /* Leaves the code highlighted so a manual copy is one keystroke away. */
    selectCode() {
      var node = this.querySelector('[data-solvex-welcome-offer-code]');
      if (!node || !window.getSelection || !document.createRange) return;

      try {
        var range = document.createRange();
        range.selectNodeContents(node);
        var selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      } catch (error) {
        // Selection is a convenience only; the code is still visible on screen.
      }
    }
  }

  if (!customElements.get('solvex-welcome-offer')) {
    customElements.define('solvex-welcome-offer', SolvexWelcomeOffer);
  }
})();
